import { useState, useRef } from "react";
import { bool, func, string } from "prop-types";
import { useTranslation } from "react-i18next";
import { getCurrencies } from "@repo/utils";
import { MenuButton, PopMenu, TextField, TooltipIcon } from "@components";
import { Grid, IconButton } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { Settings } from "@assets";
import useStyle from "./CurrencyEditor.style";
import classnames from "classnames";

const options = getCurrencies();

export const CurrencyEditor = ({
  currency,
  onChange,
  absolute,
}) => {
  const classes = useStyle();
  const { t } = useTranslation();

  const [ value, setValue ] = useState("");
  const [ visible, show ] = useState(false);
  const [ isLoading, setIsLoading ] = useState(false);

  const anchor = useRef();

  const handlePopup = () => show(!visible);
  const clickAwayHandler = () => show(false);

  const setCurrency = (data) => async () => {
    setIsLoading(true);
    await onChange(data);
    setIsLoading(false);
    
    setValue("");
    show(false);
  };

  const filtered = options
    .filter((o) => {
      o.selected = currency === o.code;

      const matcher = (v) =>
        v?.toLowerCase()?.indexOf(value.toLowerCase()) >= 0;

      return value
        ? matcher(o.name) ||
            matcher(o.code) ||
            matcher(o.symbolEnd) ||
            matcher(o.symbolStart)
        : true;
    })
    .sort((a, b) => {
      return a.selected
        ? -1
        : b.selected
        ? 1
        : a.order && b.order
        ? a.order > b.order
          ? 1
          : -1
        : a.order
        ? -1
        : b.order
        ? 1
        : a.name > b.name
        ? 1
        : -1;
    });

  return (
    <>
      <TooltipIcon
        icon={<Settings />}
        style={absolute ? { right: -8 } : undefined}
        ref={anchor}
        onClick={handlePopup}
        color="primary"
        className={classnames("noDrag text-sm", absolute && "absolute")}
      >
        {t("views.editor.dialogs.currency.title")}
      </TooltipIcon>

      <PopMenu
        show={visible}
        anchor={anchor.current}
        placement="right-start"
        onClickAway={clickAwayHandler}
        className="pt-4 px-0 pb-0"
        style={{ width: 320 }}
      >
        <Grid container direction="column">
          <Grid
            item container
            justifyContent="space-between"
            alignItems="center"
            className="mb-3 px-6"
          >
            <h3>{t("views.editor.dialogs.currency.title")}</h3>
            <IconButton
              color="primary"
              aria-label="close modal"
              size="small"
              onClick={clickAwayHandler}
            >
              <Close />
            </IconButton>
          </Grid>
          <TextField
            id="filterCurrencies"
            size="small"
            onChange={(e) => setValue(e.target.value)}
            placeholder={t("views.editor.dialogs.currency.filter")}
            disabled={isLoading}
            value={value}
            className="px-6"
            InputProps={{
              endAdornment: value && (
                <IconButton
                  aria-label="clear input"
                  color="primary"
                  onClick={() => setValue("")}
                >
                  <Close />
                </IconButton>
              ),
            }}
          />
          <div
            role="listbox"
            className={classnames(classes.list, "w-full overflow-auto pb-6")}
          >
            {
              !filtered.length &&
              <p className={classes.item}>
                {t("views.editor.dialogs.currency.not_found")}
              </p>
            }
            {
              filtered.map((o) => (
                <MenuButton
                  key={o.code}
                  className="px-6 py-2"
                  onClick={setCurrency(o)}
                  disabled={isLoading}
                >
                  {/*{`${o.symbolStart || o.symbolEnd} - ${o.name}`}*/}
                  <span className="transparent-2 mr-2 w-32">
                    {o.code}
                  </span>
                  {o.name}
                </MenuButton>
              ))
            }
          </div>
        </Grid>
      </PopMenu>
    </>
  );
}

CurrencyEditor.propTypes = {
  onChange: func.isRequired,
  currency: string,
  absolute: bool,
}